import { Entity, EntityId } from "@jackfruit/common"
import {
  Comparer,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  IdSelector,
  PayloadAction,
  SliceCaseReducers,
  Update,
  ValidateSliceCaseReducers,
} from "@reduxjs/toolkit"
import { RootState } from "./store"

// see https://redux-toolkit.js.org/api/createEntityAdapter
export const createEntitySlice = <T extends Entity>(
  name: keyof RootState,
  options: {
    selectId?: IdSelector<T>
    sortComparer?: false | Comparer<T>
  } = {},
  extraState = {},
  extraReducers?: ValidateSliceCaseReducers<any, SliceCaseReducers<any>>
) => {
  const adapter = createEntityAdapter<T>(options)

  const slice = createSlice({
    name,
    initialState: adapter.getInitialState({
      isLoading: false,
      hasError: false,
      error: "",
      ...extraState,
    }),
    reducers: {
      setIsLoading(state, action: PayloadAction<boolean>) {
        state.isLoading = action.payload
      },
      setHasError(state, action: PayloadAction<boolean>) {
        state.hasError = action.payload
      },
      setError(state, action: PayloadAction<string>) {
        state.error = action.payload
      },
      addOne(state, action: PayloadAction<T>) {
        adapter.addOne(state as EntityState<T>, action.payload)
      },
      addMany(state, action: PayloadAction<T[]>) {
        adapter.addMany(state as EntityState<T>, action.payload)
      },
      setAll(state, action: PayloadAction<T[]>) {
        adapter.setAll(state as EntityState<T>, action.payload)
      },
      removeOne(state, action: PayloadAction<EntityId>) {
        adapter.removeOne(state as EntityState<T>, action.payload)
      },
      removeMany(state, action: PayloadAction<EntityId[]>) {
        adapter.removeMany(state as EntityState<T>, action.payload)
      },
      removeAll(state) {
        adapter.removeAll(state as EntityState<T>)
      },
      updateOne(state, action: PayloadAction<Update<T>>) {
        adapter.updateOne(state as EntityState<T>, action.payload)
      },
      updateMany(state, action: PayloadAction<Update<T>[]>) {
        adapter.updateMany(state as EntityState<T>, action.payload)
      },
      upsertOne(state, action: PayloadAction<T>) {
        adapter.upsertOne(state as EntityState<T>, action.payload)
      },
      upsertMany(state, action: PayloadAction<T[]>) {
        adapter.upsertMany(state as EntityState<T>, action.payload)
      },
      ...extraReducers,
    },
  })

  const getEntities = (state: any) => state[name].entities
  const getIds = (_: any, ids: EntityId[]) => ids || []

  const makeSelectByIds = () => {
    const selectByIds = createSelector(getEntities, getIds, (entities, ids) => {
      return ids.map(id => entities[id]) as T[]
    })

    return selectByIds
  }

  const adapterSelectors = adapter.getSelectors<RootState>(
    state => state[name] as any
  ) // TODO fix this typing issue

  const selectors = {
    ...adapterSelectors,
    selectByIds: makeSelectByIds(),
  }

  return { slice, selectors }
}

/**
 * A helper function to create the parameter selectors
 * @param selector
 * @returns the parameter selector
 */
export const createParameterSelector = <T>(
  selector: (params: T) => any // @TODO Type should be of the returned param
): ((_: RootState, params: T) => ReturnType<typeof selector>) => {
  return (_, params) => selector(params)
}
